package com.bikompanion.data.rating

import dev.zacsweers.metro.Inject
import kotlinx.serialization.json.Json
import com.bikompanion.data.cache.LocalStore
import com.bikompanion.data.cache.cacheKey
import com.bikompanion.data.logRatingDiagnostic

private const val QueueKey = "pending_votes"

/** Beyond this the oldest are dropped: a queue this long means the flush is broken, not the network. */
private const val MaxQueuedVotes = 50

private val queueJson = Json { ignoreUnknownKeys = true }

/**
 * Votes cast while offline, held until a write succeeds.
 *
 * The Realtime Database SDK has a queue of its own, but not one that can be relied on here: in
 * the browser it lives in memory and dies with the page, and on Android it only survives a
 * restart with disk persistence turned on. A vote is a few seconds of a rider's attention, so
 * losing one to a page reload is worse than the small amount of machinery this costs.
 *
 * Stores the *intent* rather than the assembled update — the update contains `ServerValue`
 * sentinels that don't serialize, and a flushed vote has to be rebuilt against the moment it
 * actually lands anyway. Write-through against a single key, the same shape
 * [com.bikompanion.data.cache.BikeDataCache] uses for its blobs.
 */
@Inject
class PendingVoteQueue(
    private val store: LocalStore,
) {
    fun enqueue(vote: PendingVote) {
        val kept = (read() + vote).takeLast(MaxQueuedVotes)
        runCatching { store.write(cacheKey(QueueKey), queueJson.encodeToString(PendingVotes(kept))) }
        logRatingDiagnostic("queued count=${kept.size}")
    }

    fun read(): List<PendingVote> =
        runCatching {
            store.read(cacheKey(QueueKey))?.let { queueJson.decodeFromString<PendingVotes>(it).votes }
        }.getOrNull().orEmpty()

    fun replaceWith(votes: List<PendingVote>) {
        runCatching { store.write(cacheKey(QueueKey), queueJson.encodeToString(PendingVotes(votes))) }
    }

    fun isEmpty(): Boolean = read().isEmpty()
}
